import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, Plus, Search, X, Loader2, MapPin, User, Edit, Trash2, ChevronRight
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import ConfirmDialog from '../shared/ConfirmDialog';

const severityColors = {
  low: 'bg-yellow-100 text-yellow-700', medium: 'bg-orange-100 text-orange-700',
  high: 'bg-red-100 text-red-700', critical: 'bg-red-200 text-red-800',
};
const statusColors = {
  open: 'bg-blue-100 text-blue-700', investigating: 'bg-yellow-100 text-yellow-700',
  action_required: 'bg-red-100 text-red-700', closed: 'bg-green-100 text-green-700',
};
const categoryOptions = ['Fall', 'Medication Error', 'Pressure Injury', 'Infection', 'Near Miss', 'Equipment Failure', 'Security', 'Patient Complaint', 'Other'];
const severityOptions = ['low', 'medium', 'high', 'critical'];
const statusOptions = ['open', 'investigating', 'action_required', 'closed'];

const emptyForm = { title: '', category: '', severity: '', description: '', location: '', department: '', patient_involved: false, patient_harm: false, immediate_actions: '', status: 'open' };

const SafetyEventsPage = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadEvents = () => {
    setLoading(true);
    const params = filter !== 'all' ? `?status=${filter}` : '';
    api.get(`/api/safety-events${params}`).then(setEvents).catch(e => toast.error(e.message)).finally(() => setLoading(false));
  };
  useEffect(() => { loadEvents(); }, [filter]);

  const openCreate = () => { setForm(emptyForm); setEditingId(null); setShowModal(true); };
  const openEdit = (event) => {
    setForm({
      title: event.title || '', category: event.category || '', severity: event.severity || '',
      description: event.description || '', location: event.location || '', department: event.department || '',
      patient_involved: event.patient_involved || false, patient_harm: event.patient_harm || false,
      immediate_actions: event.immediate_actions || '', status: event.status || 'open',
    });
    setEditingId(event.id);
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!form.title || !form.category || !form.severity || !form.description || !form.location) {
      toast.error('Please fill all required fields'); return;
    }
    setSubmitting(true);
    try {
      if (editingId) {
        await api.put(`/api/safety-events/${editingId}`, form);
        toast.success('Event updated');
      } else {
        await api.post('/api/safety-events', form);
        toast.success('Event reported');
      }
      setShowModal(false); setForm(emptyForm); setEditingId(null); loadEvents();
    } catch (e) { toast.error(e.message); } finally { setSubmitting(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/api/safety-events/${deleteTarget.id}`);
      toast.success('Event deleted');
      setDeleteTarget(null); loadEvents();
    } catch (e) { toast.error(e.message); } finally { setDeleting(false); }
  };

  const filteredEvents = events.filter(e =>
    e.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    e.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    e.location?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const statusCounts = {
    open: events.filter(e => e.status === 'open').length,
    investigating: events.filter(e => e.status === 'investigating').length,
    action_required: events.filter(e => e.status === 'action_required').length,
    closed: events.filter(e => e.status === 'closed').length,
  };

  return (
    <div className="space-y-6" data-testid="safety-events-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Safety Events</h1>
          <p className="text-gray-500 text-sm mt-1">Report, track, and manage patient safety events</p>
        </div>
        <Button onClick={openCreate} className="bg-red-600 hover:bg-red-700 text-white gap-2" data-testid="report-new-event-btn">
          <Plus className="w-4 h-4" /> Report New Event
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Open', val: statusCounts.open, color: 'text-gray-900' },
          { label: 'Investigating', val: statusCounts.investigating, color: 'text-yellow-600' },
          { label: 'Action Required', val: statusCounts.action_required, color: 'text-red-600' },
          { label: 'Closed', val: statusCounts.closed, color: 'text-green-600' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl p-4 border border-gray-100">
            <p className={`text-2xl font-bold ${s.color}`} data-testid={`${s.label.toLowerCase().replace(' ', '-')}-count`}>{s.val}</p>
            <p className="text-sm text-gray-500">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Search events..." className="pl-9" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-testid="search-events-input" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {['all', 'open', 'investigating', 'action_required', 'closed'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${filter === f ? 'bg-[var(--niko-teal)] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              data-testid={`filter-${f}`}>
              {f === 'all' ? 'All' : f.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[var(--niko-teal)]" /></div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100">
          <div className="divide-y divide-gray-100">
            {filteredEvents.length === 0 ? (
              <div className="p-8 text-center text-gray-400">No safety events found</div>
            ) : filteredEvents.map(event => (
              <div key={event.id} className="p-4 hover:bg-gray-50 transition-colors group" data-testid="safety-event-item">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1 cursor-pointer" onClick={() => navigate(`/hospital/safety-events/${event.id}`)} data-testid="event-link">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-orange-100">
                      <AlertTriangle className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${severityColors[event.severity] || 'bg-gray-100'}`}>{event.severity}</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColors[event.status] || 'bg-gray-100'}`}>{(event.status || '').replace('_', ' ')}</span>
                      </div>
                      <p className="font-medium text-gray-900 group-hover:text-[var(--niko-teal)] transition-colors">{event.title}</p>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{event.location}</span>
                        <span className="flex items-center gap-1"><User className="w-3 h-3" />{event.reported_by_name}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right mr-2">
                      <p className="text-xs text-gray-400">{new Date(event.created_at).toLocaleDateString()}</p>
                      <p className="text-xs text-gray-500 mt-1">{event.category}</p>
                    </div>
                    <button onClick={() => navigate(`/hospital/safety-events/${event.id}`)} className="p-2 text-gray-400 hover:text-[var(--niko-teal)] hover:bg-[var(--niko-teal)]/10 rounded-lg opacity-0 group-hover:opacity-100 transition-all" title="View Timeline" data-testid="view-timeline-btn">
                      <ChevronRight className="w-4 h-4" />
                    </button>
                    <button onClick={() => openEdit(event)} className="p-2 text-gray-400 hover:text-[var(--niko-teal)] hover:bg-[var(--niko-teal)]/10 rounded-lg opacity-0 group-hover:opacity-100 transition-all" data-testid="edit-event-btn">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => setDeleteTarget(event)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all" data-testid="delete-event-btn">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="event-modal">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">{editingId ? 'Edit Safety Event' : 'Report Safety Event'}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-5">
              <div><Label>Title *</Label><Input className="mt-1" value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="event-title-input" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Category *</Label>
                  <select className="w-full mt-1 h-10 px-3 rounded-md border border-input bg-background text-sm" value={form.category} onChange={e => setForm({...form, category: e.target.value})} data-testid="event-category-select">
                    <option value="">Select...</option>{categoryOptions.map(c => <option key={c} value={c}>{c}</option>)}
                  </select></div>
                <div><Label>Severity *</Label>
                  <select className="w-full mt-1 h-10 px-3 rounded-md border border-input bg-background text-sm" value={form.severity} onChange={e => setForm({...form, severity: e.target.value})} data-testid="event-severity-select">
                    <option value="">Select...</option>{severityOptions.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                  </select></div>
              </div>
              {editingId && (
                <div><Label>Status</Label>
                  <select className="w-full mt-1 h-10 px-3 rounded-md border border-input bg-background text-sm" value={form.status} onChange={e => setForm({...form, status: e.target.value})} data-testid="event-status-select">
                    {statusOptions.map(s => <option key={s} value={s}>{s.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
                  </select></div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Department</Label><Input className="mt-1" value={form.department} onChange={e => setForm({...form, department: e.target.value})} /></div>
                <div><Label>Location *</Label><Input className="mt-1" value={form.location} onChange={e => setForm({...form, location: e.target.value})} data-testid="event-location-input" /></div>
              </div>
              <div><Label>Description *</Label><Textarea className="mt-1" rows={4} value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="event-description-input" /></div>
              <div><Label>Immediate Actions Taken</Label><Textarea className="mt-1" rows={3} value={form.immediate_actions} onChange={e => setForm({...form, immediate_actions: e.target.value})} /></div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.patient_involved} onChange={e => setForm({...form, patient_involved: e.target.checked})} className="rounded" />Patient involved</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.patient_harm} onChange={e => setForm({...form, patient_harm: e.target.checked})} className="rounded" />Patient harm</label>
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex gap-3">
              <Button variant="outline" onClick={() => setShowModal(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleSubmit} disabled={submitting} className="flex-1 bg-red-600 hover:bg-red-700 text-white" data-testid="submit-event-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}{editingId ? 'Update Event' : 'Submit Event'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDelete} loading={deleting}
        title="Delete Safety Event" message={`Are you sure you want to delete "${deleteTarget?.title}"? This action cannot be undone.`} />
    </div>
  );
};

export default SafetyEventsPage;
